// Library Utilities
import React, { useEffect, useRef, useState } from "react";

import PortalOverlay from "../portal-overlay/portalOverlay";
// Constants
import warningIconSvg from "../../assets/images/warning-icon.svg";

// Components
import Button from "../button/button";
import ZpinIntroBox from "./zpin-intro-box/zpinIntroBox";
import ErrorMessage from "../error-message/errorMessage";
import ZpinCreateOrUpdate from "./zpin-create-or-update/zpinCreateOrUpdate";
import HaveZpin from "./have-zpin/haveZpin";

// Styles
import Styles from "./zpinMainPopup.module.scss";
import { primaryButtonHoverStyle, primaryButtonStyle } from "../buttonStyles";

// Functions
import { checkIsEmpty, isValidOtp } from "../../utils/validation";
import { ACCENTCOLOR } from "../colors";
import { testIdProps } from "../../utils/testIdProps";

function ZpinMainPopup({
  zpinExists,
  error,
  setError,
  toggleResetOtp,
  setToggleResetOtp,
  isLoading,
  onVerifyOtp,
  onCancel,
  resendOtp,
  handleZpinAuth,
  updateZpinStatus,
  relayId,
}: any) {
  const registeredEmail = localStorage.getItem("email");
  const [invalidOTP, setInvalidOTP] = useState(false);
  const lengthOfOtp = 6;
  const [otp, setOtp] = useState("");
  const inputs: any = Array(lengthOfOtp)
    .fill(null)
    .map((_, index) => index);
  let currentActiveInput = 0;
  const inputRef = useRef<any>([]);

  // const [isValid, setIsValid] = useState(true);
  const [validOtp, setValidOtp] = useState(false);
  const [resendOTPCount, setResendOTPCount] = useState(0);
  const [timer, setTimer] = useState(0);
  const [showZpinBox, setShowZpinBox] = useState(true);
  const [createZpin, setCreateZpin] = useState(false);
  const [forUpdateFlow, setForUpdateFlow] = useState(false);
  const [forOtpFlow, setForOtpFlow] = useState(false);
  const [haveZpin, setHaveZpin] = useState(zpinExists);
  const [zPinPopup, setZpinPopup] = useState(false);
  const [otpAuthPopup, setOtpAuthPopup] = useState(!zpinExists);

  useEffect(() => {
    // reset the otp in case of any error occurred
    if (toggleResetOtp) {
      resetAllInputs();
    }
    //call api to check if the zpin is already created
  }, [toggleResetOtp]);

  function validateOtp(e: any) {
    e.preventDefault();
    if (checkIsEmpty(otp)) {
      setError("Otp cannot be empty");
      return setInvalidOTP(true);
    }
    setToggleResetOtp(false);
    onVerifyOtp(null, otp, setInvalidOTPError);
  }

  function setInvalidOTPError(errorMessage?: string) {
    setInvalidOTP(true);
    setError(errorMessage || "OTP is Invalid!, please re-enter");
    resetAllInputs();
    setTimeout(() => {
      setInvalidOTP(false);
      setError("");
    }, 2000);
  }

  function handleOtp(otpValue: string, index: number) {
    // To check whether OTP is numeric or not
    if (!isValidOtp(otpValue) && otpValue !== "") {
      inputRef.current[index].value = "";
      setError("OTP should be numeric");
      return setInvalidOTP(true);
    }
    // To check whether the user is removing the entered OTP.
    if (otpValue === "") {
      currentActiveInput = index - 1;
      const currentInput = inputRef.current[currentActiveInput];
      // To check whether the focused input is not less than 0
      if (currentActiveInput < 0) {
        return;
      }
      // To check whether the user is clearing the last input
      // if clears the last input than we remove two values else one
      if (otp.length === lengthOfOtp) {
        // const nextInput = inputRef.current[currentActiveInput + 1];
        const str = otp.substr(0, otp.length - 2);
        setOtp(str);
      } else {
        const str = otp.substr(0, otp.length - 1);
        setOtp(str);
      }
      // Remove disable state.
      currentInput.disabled = false;
      currentInput.value = "";
    } else {
      // Only accepts 6 digits in otp
      if (otp.length > lengthOfOtp - 1) {
        return;
      }
      setOtp((otp: string) => `${otp + otpValue}`);
      const currentInput = inputRef.current[index];
      // Add disable state once input is filled.
      otp.length < lengthOfOtp - 1 && (currentInput.disabled = true);
      currentActiveInput = index + 1;
    }
    // To check the verify button enable and disable
    if (otp.length === lengthOfOtp - 1) {
      setValidOtp(true);
    } else {
      setValidOtp(false);
    }
    setError("");
    const nextInput = inputRef.current[currentActiveInput];
    // To Check whether the focus does not exceed the number of input
    // else throw error of index out of bound
    if (currentActiveInput < lengthOfOtp && currentActiveInput >= 0) {
      nextInput.focus();
    }
  }

  // reset all input values in case the user selects reset password or any error occurred
  function resetAllInputs() {
    let i = 0;
    while (i < lengthOfOtp) {
      const currentInput = inputRef.current[i];
      currentInput.value = "";
      currentInput.disabled = false;
      i++;
    }
    setOtp("");
    setValidOtp(false);
    inputRef.current[currentActiveInput].focus();
  }

  function handleResendOtp(e?: React.MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();
    resetAllInputs();
    // Call API to handle Resend OTP;
    resendOtp();
    setResendOTPCount(resendOTPCount + 1);
    // Resend Otp to be enabled in
    let resendOTPInSec = 30;
    setTimer(resendOTPInSec);
    const otpCount = setInterval(() => {
      resendOTPInSec -= 1;
      setTimer(resendOTPInSec);
      if (resendOTPInSec === 0) {
        clearInterval(otpCount);
      }
    }, 1000);
    if (resendOTPCount >= 2) {
      setError("You have exhausted the resend OTP limit");
    }
  }

  function handleZpinFlow() {
    setCreateZpin(true);
    setOtpAuthPopup(false);
    setZpinPopup(true);
  }

  function handleZpinCancel() {
    setShowZpinBox(false);
    onCancel();
  }

  function updateZpinFlow() {
    setForUpdateFlow(true);
    setHaveZpin(false);
    setZpinPopup(true);
  }

  function useOtpFlowInstead(_invalidZpinCase: boolean = false) {
    // This path is only reached from HaveZpin (org already has a ZPIN),
    // including voluntary "Use OTP" and 3 failed ZPIN attempts.
    // Never show the "Introducing ZPIN" card here.
    setShowZpinBox(false);
    setOtpAuthPopup(true);
    setHaveZpin(false);
    setZpinPopup(false);
    // Always send a fresh OTP. Skipping when relayId is already set
    // (e.g. enable→disable then disable→enable) never calls /otp/send.
    resendOtp();
  }
  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pastedData = e.clipboardData.getData("Text").trim();
    const digits = pastedData
      .replace(/\D/g, "")
      .slice(0, lengthOfOtp)
      .split("");

    if (digits.length === lengthOfOtp) {
      e.preventDefault();
      setOtp(digits.join(""));

      digits.forEach((digit, i) => {
        const input = inputRef.current[i];
        if (input) {
          input.value = digit;
          input.disabled = i !== lengthOfOtp - 1; // Disable all except last
        }
      });

      const lastInput = inputRef.current[lengthOfOtp - 1];
      if (lastInput) lastInput.focus();

      setValidOtp(true);
    }
  }

  function handleZpinAuthHave(
    zpin: string,
    otp: string,
    setInvalidZpinError: any
  ) {
    handleZpinAuth(zpin, null, setInvalidZpinError);
  }

  function afterSuccessfulCreation() {
    setZpinPopup(false);
    setHaveZpin(true);
    updateZpinStatus();
  }

  return (
    <PortalOverlay>
      <div
      className={`d-flex align-items-center justify-content-center ${Styles.overlay}`}
    >
      <div className={`p-4 ${Styles.popUpContent}`} style={{ width: "500px" }}>
        {otpAuthPopup && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!validOtp) return;
              setTimer(0);
              validateOtp(e);
            }}
          >
            <div className="py-3 text-center">
              <img
                src={warningIconSvg}
                alt="otp-verification"
                style={{ width: "100px" }}
              />
            </div>
            <div className="py-2 px-4">
              <p className={`mb-0 ${Styles.headingText}`}>OTP Authentication</p>
            </div>
            <div className="px-4">
              <p className={`mb-0 ${Styles.subText}`}>
                An OTP has been sent to <span>{registeredEmail}</span>
              </p>
            </div>
            <div style={{ width: "300px", margin: "0 auto" }}>
              <div className="py-2 d-flex align-items-center justify-content-center">
                {inputs.map((input: any, index: number) => {
                  return (
                    <div key={input} className="px-1">
                      <input
                        ref={(input: any) => (inputRef.current[index] = input)}
                        {...testIdProps(`zpin-main-otp-${index}`)}
                        onClick={() => {
                          setInvalidOTP(false);
                          setError("");
                        }}
                        type="text"
                        className={`${Styles.commonInputClass} ${
                          Styles.formControl
                        } ${invalidOTP ? Styles.wrongOtp : ""}`}
                        maxLength={1}
                        autoComplete="off"
                        autoFocus={currentActiveInput === index}
                        style={{ width: "40px", top: 50, left: 50 }}
                        onKeyUp={(event: any) => {
                          const otpValue = event.target.value;
                          handleOtp(otpValue, index);
                        }}
                        onPaste={handlePaste}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-center mt-2 p-0 m-0 gap-2">
              {resendOTPCount <= 2 && (
                <div>
                  {timer > 0 ? (
                    <p style={{ fontSize: "14px" }} className={`mb-0  `}>
                      Resend OTP in {timer}:00s
                    </p>
                  ) : (
                    <button
                      type="button"
                      {...testIdProps("resend-otp")}
                      style={{
                        backgroundColor: "transparent",
                        color: ACCENTCOLOR,
                        border: "none",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        fontWeight: "500",
                        fontSize: "14px",
                        textAlign: "center",
                        textDecoration: "underline",
                      }}
                      onClick={handleResendOtp}
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              )}

              <div className="">
                <Button
                  id="submit-button"
                  type="submit"
                  hoveredStyle={primaryButtonHoverStyle}
                  style={primaryButtonStyle}
                  isLoading={isLoading}
                  disabled={!validOtp}
                  onClick={(e: any) => {
                    setTimer(0);
                    validateOtp(e);
                  }}
                >
                  Submit
                </Button>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-center text-center">
              {error && (
                <ErrorMessage
                  className="text-center  align-items-center justify-content-center d-flex"
                  forPopup={true}
                >
                  {error}
                </ErrorMessage>
              )}
            </div>
            {showZpinBox && <ZpinIntroBox handleZpinFlow={handleZpinFlow} />}
            <div className="py-4 d-flex justify-content-center">
              <div className="d-inline-flex">
                <p
                  {...testIdProps("cancel")}
                  className={`mb-0 ${Styles.cancelText}`}
                  onClick={() => {
                    setTimer(0);
                    onCancel();
                  }}
                >
                  Cancel
                </p>
              </div>
            </div>
          </form>
        )}
        {haveZpin && (
          <HaveZpin
            updateZpinFlow={updateZpinFlow}
            handleZpinAuthHave={handleZpinAuthHave}
            onCancel={onCancel}
            isLoading={isLoading}
            otpFlowInstead={useOtpFlowInstead}
          />
        )}

        {zPinPopup && (
          <ZpinCreateOrUpdate
            forUpdate={forUpdateFlow}
            forOtp={forOtpFlow}
            afterSuccessfulCreation={afterSuccessfulCreation}
            handleZpinAuth={(
              zpin: string,
              otp: string,
              setInvalidZpinError: any
            ) => handleZpinAuth(zpin, otp, setInvalidZpinError)}
            onCancel={() => handleZpinCancel()}
            revertBackToOTP={() => setShowZpinBox(false)}
            resendOtp={resendOtp}
            relayId={relayId}
            onVerifyOtp={onVerifyOtp}
          />
        )}
      </div>
    </div></PortalOverlay>
  );
}

export default ZpinMainPopup;
